import React, { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getTwitterAuthUrl, getDiscordLoginUrl } from '../services/authService';
import { getOAuthStartedAt, removeOAuthStartedAt, setOAuthStartedAt } from '../services/sessionStorage';

const OAUTH_START_COOLDOWN_MS = 30 * 1000;

const LOGIN_ERROR_MESSAGES = {
  creator_not_found: 'This X account is not connected to a creator profile yet.',
  invalid_state: 'Your login session expired. Please try signing in again.',
  missing_params: 'X did not return the login details we expected. Please try again.',
  oauth_denied: 'X login was cancelled or denied.',
  oauth_failed: 'X login failed. Please try again.',
  oauth_init_failed: 'Could not start X login. Please try again.',
  rate_limited: 'Too many login attempts. Please wait a few minutes and try again.',
  discord_not_configured: 'Discord recovery is temporarily unavailable. Please contact support.',
  discord_invalid_state: 'Your Discord sign-in expired. Start again from this browser.',
  discord_failed: 'Discord sign-in failed. Please try again.',
  discord_recovery_unavailable: 'This Discord account has no single previously verified creator profile. Sign in with X or ask support to review your account.'
};

export default function LoginPage() {
  const { creator, token, loading, authError, retryAuth, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const [startingTwitterLogin, setStartingTwitterLogin] = useState(false);
  const loginError = searchParams.get('error');
  const loginErrorDetail = searchParams.get('detail');

  if (loading) return null;
  if (creator) return <Navigate to="/dashboard" replace />;

  const startTwitterLogin = () => {
    if (startingTwitterLogin) return;

    if (loginError) {
      removeOAuthStartedAt();
    }

    const lastStarted = getOAuthStartedAt();
    const remainingCooldown = OAUTH_START_COOLDOWN_MS - (Date.now() - lastStarted);
    if (remainingCooldown > 0) {
      setStartingTwitterLogin(true);
      setTimeout(() => setStartingTwitterLogin(false), remainingCooldown);
      return;
    }

    setOAuthStartedAt(Date.now());
    setStartingTwitterLogin(true);
    window.location.assign(getTwitterAuthUrl());
  };

  const watermarkRows = Array(8).fill(
    'TAKEOVER TAKEOVER TAKEOVER TAKEOVER TAKEOVER TAKEOVER'
  );

  return (
    <div className="login-screen">
      <div className="login-bg">
        {watermarkRows.map((row, i) => (
          <span key={i}>{row}</span>
        ))}
      </div>
      <div className="login-box">
        <div className="login-logo">TAKEOVER</div>
        <div className="login-sub">Creator Portal</div>
        {!creator && token && authError && (
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ color: 'var(--red)', fontFamily: "'Space Mono', monospace", fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 16 }}>
              {authError}
            </div>
            <button className="login-btn primary" onClick={retryAuth}>
              Retry Session
            </button>
            <button className="topnav-mobile-logout" onClick={logout} style={{ marginTop: 12 }}>
              Sign Out
            </button>
          </div>
        )}
        {loginError && (
          <div style={{ color: 'var(--red)', fontFamily: "'Space Mono', monospace", fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', textAlign: 'center', marginBottom: 16 }}>
            {LOGIN_ERROR_MESSAGES[loginError] || 'Login failed. Please try again.'}
            {loginErrorDetail && (
              <div style={{ color: '#777', marginTop: 8, overflowWrap: 'anywhere' }}>
                {loginErrorDetail}
              </div>
            )}
          </div>
        )}
        <div className="login-methods" style={{ display: token && authError ? 'none' : undefined }}>
          <button
            className="login-btn primary"
            disabled={startingTwitterLogin}
            onClick={startTwitterLogin}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            {startingTwitterLogin ? 'Opening X...' : 'Sign in with X (Twitter)'}
          </button>
          <button className="login-btn" onClick={() => window.location.assign(getDiscordLoginUrl())}>
            Sign in with linked Discord
          </button>
          <p style={{ color: '#999', fontSize: 12, lineHeight: 1.6 }}>
            Lost access to X? Use a Discord account you previously verified here to recover your existing profile.
          </p>
        </div>
        <div className="login-footer">wetakeover.xyz · Powered by Bricktopians</div>
      </div>
    </div>
  );
}
